import { useEffect, useState } from "react";
import { ConversationVariable, Message } from "../types/types";
import { useConversationData } from "./useConversationData";
import { getAllBubblesCount } from "../utils/getAllBubblesCount";

export function useConversation({
    lang,
    conversationName,
    toggleVariables,
    variablesRef,
}: {
    lang: string;
    conversationName: string;
    toggleVariables: (variables: { name: string; value: string }[]) => void;
    variablesRef: React.RefObject<ConversationVariable[]>;
}) {
    const { conversation: conversationData, loading } = useConversationData({
        lang,
        conversationName,
    });
    const [messages, setMessages] = useState<Message[]>([]);
    const [lastSelectedQuestion, setLastSelectedQuestion] = useState<{
        text: string;
        sectionId: string;
    } | null>(null);

    const [availableQuestions, setAvailableQuestions] = useState<
        Array<{
            text: string;
            sectionId: string;
        }>
    >([]);

    const [poll, setPoll] = useState<{
        pollId: string | null;
        options: Array<{
            text: string;
            optionId: string;
        }>;
    }>({
        pollId: null,
        options: [],
    });

    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
    const [currentPartIndex, setCurrentPartIndex] = useState(0);

    const bubbleCount = getAllBubblesCount(messages);

    // Calculate total parts shown so far
    const currentBubbleCount =
        messages
            .slice(0, currentMessageIndex)
            .reduce((acc, msg) => acc + msg.body.length, 0) +
        currentPartIndex +
        1;

    const allBubblesVisible = bubbleCount === currentBubbleCount;

    const shouldSkipPart = (partType: string) => {
        return (
            partType === "variable" ||
            partType === "reward" ||
            partType === "link"
        );
    };

    const advanceToNextPart = (messages: Message[]) => {
        setCurrentMessageIndex((prevMessageIndex) => {
            if (messages[prevMessageIndex] === undefined) {
                return prevMessageIndex;
            }

            const currentMessage = messages[prevMessageIndex];
            if (
                currentMessage &&
                currentMessage.body.length > currentPartIndex + 1
            ) {
                // Stay in the same message, just advance part index
                setCurrentPartIndex((prevPartIndex) => {
                    let partIndex = prevPartIndex + 1;
                    const variablesToToggle = [];

                    // Collect all consecutive skippable parts
                    while (partIndex < currentMessage.body.length) {
                        const part = currentMessage.body[partIndex];
                        if (part !== undefined && shouldSkipPart(part.type)) {
                            // Collect variables to toggle
                            if (part.type === "variable") {
                                variablesToToggle.push({
                                    name: part.name,
                                    value: part.value,
                                });
                            }
                            partIndex++;
                        } else {
                            break;
                        }
                    }

                    // Toggle all collected variables before showing the next visible part
                    if (variablesToToggle.length > 0) {
                        toggleVariables(variablesToToggle);
                    }

                    // If we processed any skippable parts, return the first non-skippable index
                    // Otherwise return the original next index
                    if (partIndex > prevPartIndex + 1) {
                        // We skipped at least one part
                        return partIndex < currentMessage.body.length
                            ? partIndex
                            : partIndex - 1; // Stay at last part if we reached the end
                    }

                    return prevPartIndex + 1;
                });
                return prevMessageIndex;
            }

            if (!messages[prevMessageIndex + 1]) {
                // No more messages to show, for now
                return prevMessageIndex;
            }

            const newMessageIndex = prevMessageIndex + 1;
            const newMessage = messages[newMessageIndex];

            if (!newMessage) {
                return prevMessageIndex;
            }

            // Advance to next message and process all consecutive skippable parts
            let partIndex = 0;
            const variablesToToggle = [];

            while (partIndex < newMessage.body.length) {
                const part = newMessage.body[partIndex];
                if (part !== undefined && shouldSkipPart(part.type)) {
                    // Collect variables to toggle
                    if (part.type === "variable") {
                        variablesToToggle.push({
                            name: part.name,
                            value: part.value,
                        });
                    }
                    partIndex++;
                } else {
                    break;
                }
            }

            // Toggle all collected variables before showing the next visible part
            if (variablesToToggle.length > 0) {
                toggleVariables(variablesToToggle);
            }

            // Set index to first non-skippable part (or 0 if none were skippable)
            setCurrentPartIndex(partIndex);

            return newMessageIndex;
        });
    };

    const handleNewMessages = (newMessages: Message[]) => {
        setMessages((prev) => {
            const messages: Message[] = [...prev, ...newMessages];

            advanceToNextPart(messages);

            return messages;
        });
    };

    useEffect(() => {
        const conversation = conversationData?.conversation;

        if (!conversation) return;

        const allVariables = conversation.variables;

        const startId = conversation.start.sectionId;

        const startSection = conversation.sections.find((section) => {
            if (section.id !== startId) return false;
            if (!section.condition) return true;

            const sectionConditionName = section.condition.variable;
            const sectionConditionValue = section.condition.value;

            const globalVariable =
                variablesRef.current?.find(
                    (variable) => variable.name === sectionConditionName,
                ) ||
                allVariables?.find(
                    (variable) => variable.name === sectionConditionName,
                );

            return globalVariable?.value === sectionConditionValue;
        });

        if (startSection) {
            setMessages([
                {
                    from: "character",
                    body: startSection.body,
                },
            ]);

            // Process all consecutive skippable parts at the start
            let partIndex = 0;
            const variablesToToggle = [];

            while (partIndex < startSection.body.length) {
                const part = startSection.body[partIndex];
                if (part !== undefined && shouldSkipPart(part.type)) {
                    // Collect variables to toggle
                    if (part.type === "variable") {
                        variablesToToggle.push({
                            name: part.name,
                            value: part.value,
                        });
                    }
                    partIndex++;
                } else {
                    break;
                }
            }

            // Toggle all collected variables before showing the first visible part
            if (variablesToToggle.length > 0) {
                toggleVariables(variablesToToggle);
            }

            // Set index to first non-skippable part (or 0 if none were skippable)
            if (partIndex > 0) {
                setCurrentPartIndex(partIndex);
            }

            setAvailableQuestions(
                startSection.questions !== undefined
                    ? (startSection.questions.filter((question) => {
                          return question.sectionId !== undefined;
                      }) as Array<{
                          text: string;
                          sectionId: string;
                      }>)
                    : [],
            );
        }
    }, [conversationData]);

    const conversation = conversationData?.conversation;

    const selectQuestion = (question: { text: string; sectionId: string }) => {
        if (!conversation) return;

        const nextSection = conversation.sections.find((section) => {
            if (section.id !== question.sectionId) return false;
            if (!section.condition) return true;

            const sectionConditionName = section.condition.variable;
            const sectionConditionValue = section.condition.value;

            const globalVariable =
                variablesRef.current?.find(
                    (variable) => variable.name === sectionConditionName,
                ) ||
                conversation.variables?.find(
                    (variable) => variable.name === sectionConditionName,
                );

            return globalVariable?.value === sectionConditionValue;
        });

        if (nextSection) {
            handleNewMessages([
                {
                    from: "character",
                    body: nextSection.body,
                },
            ]);

            setAvailableQuestions(
                nextSection.questions !== undefined
                    ? (nextSection.questions.filter((question) => {
                          return question.sectionId !== undefined;
                      }) as Array<{
                          text: string;
                          sectionId: string;
                      }>)
                    : [],
            );
            setLastSelectedQuestion(question);

            if (nextSection.pollId && nextSection.pollOptions) {
                setPoll({
                    pollId: nextSection.pollId,
                    options: nextSection.pollOptions,
                });
            } else {
                setPoll({
                    pollId: null,
                    options: [],
                });
            }
        }
    };

    return {
        conversation,
        messages,
        availableQuestions,
        currentMessageIndex,
        currentPartIndex,
        allBubblesVisible,
        lastSelectedQuestion,
        loading,
        poll,
        toggleVariables,
        selectQuestion,
        advanceToNextPart: () => advanceToNextPart(messages),
    };
}
